import { apiWrite } from "@/api/client";
import type { ConfirmRefusal } from "@/components/ConfirmDialog";
import {
  doneState,
  errorState,
  type FormState,
  parseFormData,
} from "@/lib/formState";
import {
  type InlineCreateResult,
  inlineFieldError,
  parseInline,
} from "@shared/inlineCreate";
import { formatTemplateRefs } from "@shared/usage";
import {
  topicCreateBody,
  topicSchema,
  topicUpdateSchema,
} from "@shared/validation";
import { refusalFrom } from "./refusal";

export async function createTopicAction(
  _prevState: FormState,
  formData: FormData,
): Promise<FormState> {
  const parsed = parseFormData(topicSchema, formData);

  if (!parsed.wasParsed) {
    return parsed.state;
  }

  const result = await apiWrite<{ id: number }>("/topics", {
    method: "POST",
    body: parsed.value,
  });

  if (!result.isOk) {
    return errorState(parsed.values, {
      // A deactivated topic still holds its code, so the listing may show
      // nothing active under it while the insert still collides.
      code: ["A topic with this code already exists (it may be inactive)."],
    });
  }

  return doneState("/topics");
}

export type TopicInlineChoice = {
  id: number;
  code: string;
  label: string;
};

// Minted from the quest template form. Sort order stays 0, which puts the new
// shelf first in the browse list — the ordering is a decision about the whole
// shelf wall, not about the quest being written, so it is made on /topics.
export async function createTopicInlineAction(input: {
  code: string;
  label: string;
}): Promise<InlineCreateResult<TopicInlineChoice>> {
  const parsed = parseInline(topicCreateBody, input);

  if (!parsed.wasParsed) {
    return { wasCreated: false, fieldErrors: parsed.fieldErrors };
  }

  const result = await apiWrite<{ id: number }>("/topics", {
    method: "POST",
    body: parsed.value,
  });

  if (!result.isOk) {
    return inlineFieldError(
      "code",
      "A topic with this code already exists (it may be inactive).",
    );
  }

  return {
    wasCreated: true,
    choice: {
      id: result.value.id,
      code: parsed.value.code,
      label: parsed.value.label,
    },
  };
}

export async function updateTopicAction(
  id: number,
  _prevState: FormState,
  formData: FormData,
): Promise<FormState> {
  const parsed = parseFormData(topicUpdateSchema, formData);

  if (!parsed.wasParsed) {
    return parsed.state;
  }

  await apiWrite(`/topics/${id}`, { method: "PUT", body: parsed.value });

  return doneState("/topics");
}

export async function deactivateTopicAction(
  id: number,
): Promise<ConfirmRefusal> {
  await apiWrite(`/topics/${id}/deactivate`, { method: "POST" });

  return null;
}

export async function restoreTopicAction(id: number): Promise<void> {
  await apiWrite(`/topics/${id}/activate`, { method: "POST" });
}

// The dialog only opens on deactivated rows, but the checks run again
// server-side: a template pointed here since the listing rendered refuses in
// place.
export async function deleteTopicAction(id: number): Promise<ConfirmRefusal> {
  const result = await apiWrite(`/topics/${id}`, { method: "DELETE" });

  return refusalFrom(result, {
    inUse: (templates) =>
      `Quest templates still point at this topic: ${formatTemplateRefs(templates)}. Delete or retarget them first.`,
    missing: "This topic no longer exists.",
    stillActive:
      "This topic has been restored since the page loaded. Deactivate it again first.",
  });
}
